import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { towerSeed } from '../../game/fortress/gen';
import { fetchAssignments } from '../../lib/api';
import { chartsFor, defaultCharts, type Chart } from '../../lib/grammar/charts';
import { sfxRare } from '../../game/sfx';
import { RARITY, speciesById, speciesImage, type Rarity } from '../../lib/aquarium';
import { burst } from '../../lib/confetti';
import { weekLabel } from '../../lib/dates';
import { carById } from '../../game/cars';
import { enterTower, finishTower, TOWER_COST, towerBoard, towerFloor, type TowerReward, type TowerRow } from '../../lib/tower';

// ─────────────────────────────────────────────────────────────────────────────
// Ocean → 🏰 Fortress: this week's Sunken Fortress, a 3D Zelda-style dungeon.
// Every Monday a new 3-floor fortress (the same for the whole class), its
// puzzles built from the verbs the class has been assigned; 🪙 3 a try; the
// first clear each week wins a rare fish and a new race car (never sand
// dollars: those only come from the teacher and class work).
// Rules: 20261040_sunken_tower.sql, 20261041.
// ─────────────────────────────────────────────────────────────────────────────

// The 3D game (and three.js with it) only downloads when someone goes in.
const FortressGame = lazy(() => import('../../components/FortressGame'));

type Props = { cid: string; language: string; look: unknown; balance: number; setBalance: (b: number) => void; teacher?: boolean };
type Playing = { run: string | null; week: string };
type Result = { won: boolean; floor: number; secs: number; reward: TowerReward | null; error?: string };

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/**
 * Every verb chart from the class's grammar assignments that are out (not drafts, not scheduled
 * for later), so everyone in the class gets the same dungeon. None yet: the present tense.
 */
async function classCharts(cid: string, language: string): Promise<Chart[]> {
  const today = new Date().toLocaleDateString('en-CA');
  const out = (await fetchAssignments(cid))
    .filter(a => a.kind === 'grammar' && a.grammar && !a.draft && a.week_start <= today && (!a.publish_on || a.publish_on <= today))
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
    .flatMap(a => chartsFor(a.grammar!));
  const seen = new Set<string>();
  const charts = out.filter(c => { const k = `${c.verb}|${c.tense}`; return seen.has(k) ? false : (seen.add(k), true); });
  return charts.length ? charts : defaultCharts(language);
}

export default function Fortress({ cid, language, look, balance, setBalance, teacher }: Props) {
  const [board, setBoard] = useState<{ week: string; board: TowerRow[] } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState<Playing | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const ended = useRef(false);
  const [charts, setCharts] = useState<Chart[] | null>(null);

  const load = useCallback(async () => {
    try { setBoard(await towerBoard(cid)); setError(''); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not open the fortress.'); }
  }, [cid]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { classCharts(cid, language).then(setCharts, () => setCharts(defaultCharts(language))); }, [cid, language]);

  const start = async () => {
    setBusy(true); setError('');
    try {
      if (teacher) setPlaying({ run: null, week: board?.week ?? '' });   // the teacher tries it for free; nothing is recorded
      else { const r = await enterTower(cid); setBalance(r.balance); setPlaying({ run: r.run, week: r.week }); }
      ended.current = false; setResult(null);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not get in.'); }
    finally { setBusy(false); }
  };

  const end = async (won: boolean, floor: number, secs: number) => {
    if (ended.current || !playing) return;
    ended.current = true;
    setResult({ won, floor, secs, reward: null });
    if (won) { sfxRare(); burst(); }
    if (!playing.run) return;
    try {
      const reward = await finishTower(playing.run, won);
      if (reward.balance != null) setBalance(reward.balance);
      setResult({ won, floor, secs, reward });
    } catch (e) { setResult({ won, floor, secs, reward: null, error: e instanceof Error ? e.message : 'Could not save your run.' }); }
    void load();
  };
  const quit = () => {
    if (playing && !ended.current && playing.run) void finishTower(playing.run, false).then(() => load()).catch(() => {});
    setPlaying(null); setResult(null);
  };

  if (playing) {
    const r = result;
    const fish = r?.reward?.species ? speciesById(r.reward.species) : null;
    return (
      <Suspense fallback={<div className="panel muted">Opening the fortress…</div>}>
      <FortressGame
        seed={towerSeed(cid, playing.week)} look={look} charts={charts ?? []}
        onEnd={(w, f, s) => void end(w, f, s)}
        onFloor={n => { if (playing.run && n > 1) void towerFloor(playing.run, n); }}
        onQuit={quit}
        result={r && (
          <>
            <h2>{r.won ? '👑 You found the treasure!' : `💀 Knocked out on floor ${r.floor}`}</h2>
            <p>⏱ {mmss(r.secs)}</p>
            {r.won && r.reward && (r.reward.species || r.reward.car) && (
              <div className="tower-prize">
                {r.reward.car && <p><span style={{ fontSize: '2rem' }}>{carById(r.reward.car).icon}</span><br /><strong>New car: {carById(r.reward.car).name}!</strong><br /><span className="small muted">{carById(r.reward.car).blurb} Pick it before a race.</span></p>}
                {fish && (
                  <p>
                    <img src={speciesImage(fish.id)} alt="" width={64} style={{ borderRadius: 10, border: `3px solid ${RARITY[fish.rarity as Rarity].color}` }} /><br />
                    <strong>{fish.name}</strong> ({RARITY[fish.rarity as Rarity].name}){r.reward.new ? ' · new in your Fishpedia!' : ''}<br />
                    <span className="small muted">It's in your bucket in the Ocean.</span>
                  </p>
                )}
              </div>
            )}
            {r.won && r.reward && !r.reward.species && !r.reward.car && <p className="small muted">You already got this week's treasure. A new fortress rises on Monday!</p>}
            {r.won && !playing.run && <p className="small muted">(Teacher preview: nothing was saved.)</p>}
            {r.error && <p className="small bad">{r.error}</p>}
            <div className="row" style={{ justifyContent: 'center', gap: 8 }}>
              <button type="button" className="primary" onClick={quit}>Done</button>
            </div>
          </>
        )}
      />
      </Suspense>
    );
  }

  const rows = board?.board ?? [];
  const mine = rows.find(r => r.me);
  return (
    <div className="tower-card">
      <div className="tower-hero">
        <h2>🏰 The Sunken Fortress</h2>
        <div className="small" style={{ opacity: 0.8 }}>{board ? `Week of ${weekLabel(board.week)} · a new fortress every Monday` : 'A new fortress every Monday'}</div>
        <ul className="small">
          <li>Three floors in 3D, seen from just behind your own fish, from the Coral Halls down to the Kraken’s Vault. The treasure is behind the Kraken.</li>
          <li>🧱 Push giant stone blocks carved with verbs into the holes of their pronouns. A wrong hole spits the block back out, with a crab!</li>
          <li>🪞 Turn mirrors to shine light on the right answer, ☀️ shoot light arrows into sun plaques, 🦶 step on the right forms, 💎 strike the right crystal.</li>
          <li>Fight 🦀 crabs, jellyfish, puffers and urchins with your trident and bow. 👁️ Eye sentries never move but fire lasers: hide behind blocks and pillars, shoot their eye.</li>
          <li>Climb hills, find the key 🔑 to each floor’s locked door, and take the stairs down. Six hearts for the whole dive.</li>
        </ul>
        <div className="row" style={{ marginTop: 12, gap: 10 }}>
          <button type="button" className="primary" disabled={busy || !charts || (!teacher && balance < TOWER_COST)} onClick={() => void start()}>
            {teacher ? '▶ Try it (free for teachers)' : `⚔️ Enter for 🪙 ${TOWER_COST}`}
          </button>
          {!teacher && <span className="small">{mine?.won ? '✅ You beat this week’s fortress!' : 'First clear this week: a rare fish and a new race car'}</span>}
        </div>
        {!teacher && balance < TOWER_COST && <p className="small" style={{ marginBottom: 0 }}>You need 🪙 {TOWER_COST}. Earn sand dollars by doing your homework!</p>}
        {error && <p className="small bad" style={{ marginBottom: 0 }}>{error}</p>}
      </div>
      <div className="panel">
        <h3 style={{ marginTop: 0 }}>🏆 This week</h3>
        {rows.length === 0 ? <p className="muted small">Nobody has gone in yet. Be the first!</p> : (
          <table className="tower-board">
            <thead><tr><th>#</th><th>Fish</th><th>Best</th><th>Tries</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className={r.me ? 'me' : ''}>
                  <td>{i + 1}</td>
                  <td>{r.name ?? 'A classmate'}{r.me ? ' (you)' : ''}</td>
                  <td>{r.won ? `👑 ${mmss(r.secs ?? 0)}` : `Floor ${r.floor}`}</td>
                  <td>{r.tries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
